/** Whether a failed request was the free plan's weekly new-project quota (HTTP 403, `error_code`). */
export const isProjectQuotaExceeded = (error) =>
  (error?.response?.data ?? error?.data)?.error_code === 'quota_exceeded';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** The quota as it stands at `now`: past its reset it is a fresh week (nothing used, next reset a whole number of weeks on). */
export const currentProjectQuota = (quota, now = Date.now()) => {
  if (!quota) return null;
  const resetsAt = Date.parse(quota.resets_at);
  if (!(now >= resetsAt)) return quota;
  const weeksPast = Math.floor((now - resetsAt) / WEEK_MS) + 1;
  return {
    ...quota,
    used: 0,
    remaining: quota.limit,
    resets_at: new Date(resetsAt + weeksPast * WEEK_MS).toISOString(),
  };
};

/** Whether `GET /user`'s `weekly_project_quota` says no projects are left before it resets. */
export const isProjectQuotaExhausted = (quota, now = Date.now()) =>
  (currentProjectQuota(quota, now)?.remaining ?? 1) <= 0;

/** Local reset time, e.g. "Sunday at 5:00 PM"; `withDate` gives "Sunday, October 18 at 5:00 PM". Null if unparseable. */
export const formatQuotaResetsAt = (resetsAt, { withDate = false } = {}) => {
  const date = new Date(resetsAt);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString(
    undefined,
    withDate ? { weekday: 'long', month: 'long', day: 'numeric' } : { weekday: 'long' }
  );
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${time}`;
};

/** "resource" / "resources" for a count. */
export const resourceNoun = (count) => (count === 1 ? 'resource' : 'resources');

/** The user with one more project counted, so the pre-submit check stays current without a refetch. */
export const withProjectCounted = (user, now = Date.now()) => {
  // Roll a stale week over first, or the count lands on last week's total.
  const quota = currentProjectQuota(user?.weekly_project_quota, now);
  if (!quota) return user;
  const used = quota.used + 1;
  return {
    ...user,
    weekly_project_quota: { ...quota, used, remaining: Math.max(quota.limit - used, 0) },
  };
};

/** The user marked out of projects from a quota_exceeded body, so the next submit is caught up front. */
export const withProjectQuotaExhausted = (user, { limit, resets_at: resetsAt } = {}) => {
  if (!user || !limit || !resetsAt) return user;
  return {
    ...user,
    weekly_project_quota: { limit, used: limit, remaining: 0, resets_at: resetsAt },
  };
};
